/**
 * Sidebar Hub
 * Single scrollable panel: Newsletter + Discount, Recently Viewed, Delivery Tracker, Save Basket
 */
(function () {
  'use strict';

  var SidebarHub = (function () {

    var STORAGE_KEY_OPEN       = 'sidebar-hub-open';
    var STORAGE_KEY_SUBSCRIBED = 'sidebar-hub-subscribed';
    var RECENTLY_VIEWED_KEY    = 'recently-viewed';
    var MAX_RECENT_PRODUCTS    = 6;

    function SidebarHub(container) {
      if (!container) return;

      this.container = container;
      this.panel     = document.getElementById('SidebarHubPanel');
      this.trigger   = document.getElementById('SidebarHubTrigger');
      this.closeBtn  = document.getElementById('SidebarHubClose');
      this.backdrop  = document.getElementById('SidebarHubBackdrop');

      if (!this.panel || !this.trigger) return;

      this._setScrollbarOffset();
      this._bindEvents();
      this._restoreState();
    }

    SidebarHub.prototype = Object.assign({}, SidebarHub.prototype, {

      /* ---------------------------------------------------------------
         Scrollbar offset — keeps trigger clear of the OS scrollbar
      --------------------------------------------------------------- */
      _setScrollbarOffset: function () {
        var width = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.setProperty('--hub-scrollbar-width', width + 'px');
      },

      /* ---------------------------------------------------------------
         Events
      --------------------------------------------------------------- */
      _bindEvents: function () {
        var self = this;

        this.trigger.addEventListener('click', function () {
          self.isOpen() ? self.close() : self.open();
        });

        if (this.closeBtn) {
          this.closeBtn.addEventListener('click', function () {
            self.close();
          });
        }

        if (this.backdrop) {
          this.backdrop.addEventListener('click', function () {
            self.close();
          });
        }

        // Copy discount code button
        this.container.addEventListener('click', function (e) {
          var copyBtn = e.target.closest('[data-copy-target]');
          if (!copyBtn) return;
          var codeEl = document.getElementById(copyBtn.getAttribute('data-copy-target'));
          if (!codeEl) return;
          self._copyToClipboard(codeEl.textContent.trim(), copyBtn);
        });

        // Close on Escape key
        document.addEventListener('keydown', function (e) {
          if ((e.key === 'Escape' || e.keyCode === 27) && self.isOpen()) {
            self.close();
          }
        });
      },

      /* ---------------------------------------------------------------
         Open / Close
      --------------------------------------------------------------- */
      isOpen: function () {
        return !this.panel.hidden;
      },

      open: function () {
        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.container.classList.add('is-open');
        document.body.classList.add('sidebar-hub-open');
        this._setSession(STORAGE_KEY_OPEN, '1');

        // Load dynamic content now that the panel is visible
        this._initRecentlyViewed();
        this._initDeliveryTracker();

      },

      close: function () {
        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.container.classList.remove('is-open');
        document.body.classList.remove('sidebar-hub-open');
        this._setSession(STORAGE_KEY_OPEN, '0');
      },

      /* ---------------------------------------------------------------
         Restore state from sessionStorage
      --------------------------------------------------------------- */
      _restoreState: function () {
        // If newsletter was just successfully submitted, auto-open to the newsletter section
        var successEl = this.container.querySelector('[data-newsletter-success]');
        if (successEl) {
          this._setLocal(STORAGE_KEY_SUBSCRIBED, '1');
          this.open();
          return;
        }

        // Restore open state from the previous page visit
        if (this._getSession(STORAGE_KEY_OPEN) === '1') {
          this.open();
        }
      },

      /* ---------------------------------------------------------------
         Newsletter — copy discount code
      --------------------------------------------------------------- */
      _copyToClipboard: function (text, btn) {
        var self = this;

        if (!navigator.clipboard) {
          var ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity  = '0';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          try { document.execCommand('copy'); } catch (e) {}
          document.body.removeChild(ta);
          self._showCopyFeedback(btn);
          return;
        }

        navigator.clipboard.writeText(text).then(function () {
          self._showCopyFeedback(btn);
        });
      },

      _showCopyFeedback: function (btn) {
        var span = btn.querySelector('span');
        if (!span) return;
        var original = span.textContent;
        span.textContent = 'Copied!';
        btn.disabled = true;
        setTimeout(function () {
          span.textContent = original;
          btn.disabled = false;
        }, 2000);
      },

      /* ---------------------------------------------------------------
         Recently Viewed
      --------------------------------------------------------------- */
      _recentlyViewedLoaded: false,

      _initRecentlyViewed: function () {
        if (this._recentlyViewedLoaded) return;
        this._recentlyViewedLoaded = true;

        var container = document.getElementById('SidebarHubRecentlyViewed');
        if (!container) return;

        var emptyMsg  = container.getAttribute('data-empty-msg') || 'No recently viewed products yet.';
        var searchUrl = container.getAttribute('data-routes-search') || '/search';

        var rawIds = null;
        try { rawIds = window.localStorage.getItem(RECENTLY_VIEWED_KEY); } catch (e) {}

        if (!rawIds) {
          container.innerHTML = '<p class="sidebar-hub__empty-msg">' + emptyMsg + '</p>';
          return;
        }

        var ids;
        try { ids = JSON.parse(rawIds); } catch (e) { ids = []; }

        // Exclude the current product if on a product page
        var currentEl = document.querySelector('[data-product-id]');
        var currentId = currentEl ? currentEl.getAttribute('data-product-id') : null;
        if (currentId) {
          ids = ids.filter(function (id) { return String(id) !== String(currentId); });
        }

        ids = ids.slice(0, MAX_RECENT_PRODUCTS);

        if (!ids.length) {
          container.innerHTML = '<p class="sidebar-hub__empty-msg">' + emptyMsg + '</p>';
          return;
        }

        var query = ids.map(function (id) { return 'id:' + id; }).join(' OR ');
        var url   = searchUrl + '?view=recently-viewed&type=product&q=' + encodeURIComponent(query);

        fetch(url)
          .then(function (res) { return res.text(); })
          .then(function (html) {
            var parser = new DOMParser();
            var doc    = parser.parseFromString(html, 'text/html');
            var grid   = doc.querySelector('.product-grid, .new-grid');

            if (grid && grid.querySelector('.grid-product')) {
              var wrap = document.createElement('div');
              wrap.className = 'sidebar-hub__rv-carousel';
              wrap.appendChild(grid);
              container.innerHTML = '';
              container.appendChild(wrap);
            } else {
              container.innerHTML = '<p class="sidebar-hub__empty-msg">' + emptyMsg + '</p>';
            }
          })
          .catch(function () {
            container.innerHTML = '<p class="sidebar-hub__empty-msg">' + emptyMsg + '</p>';
          });
      },

      /* ---------------------------------------------------------------
         Delivery Tracker
      --------------------------------------------------------------- */
      _deliveryLoaded: false,

      _initDeliveryTracker: function () {
        if (this._deliveryLoaded) return;
        this._deliveryLoaded = true;

        var wrapper      = document.getElementById('SidebarHubDelivery');
        var bar          = document.getElementById('SidebarHubDeliveryBar');
        var msg          = document.getElementById('SidebarHubDeliveryMsg');
        if (!wrapper || !bar || !msg) return;

        var threshold    = parseInt(wrapper.getAttribute('data-threshold'), 10) || 0;
        var progressText = wrapper.getAttribute('data-progress-text') || 'Spend {remaining} more for free delivery!';
        var achievedText = wrapper.getAttribute('data-achieved-text') || 'You have qualified for free delivery!';

        fetch('/cart.js')
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            var total     = cart.total_price;
            var remaining = Math.max(0, threshold - total);
            var pct       = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 100;

            bar.style.width = pct + '%';
            bar.setAttribute('aria-valuenow', pct);

            if (remaining <= 0) {
              msg.textContent = achievedText;
              wrapper.classList.add('is-achieved');
            } else {
              var formatted   = '\u00a3' + (remaining / 100).toFixed(2);
              msg.textContent = progressText.replace('{remaining}', formatted);
            }
          })
          .catch(function () {
            msg.textContent = progressText.replace('{remaining}', '\u2014');
          });
      },

      /* ---------------------------------------------------------------
         Storage helpers
      --------------------------------------------------------------- */
      _setSession: function (key, val) {
        try { sessionStorage.setItem(key, val); } catch (e) {}
      },

      _getSession: function (key) {
        try { return sessionStorage.getItem(key); } catch (e) { return null; }
      },

      _setLocal: function (key, val) {
        try { localStorage.setItem(key, val); } catch (e) {}
      },

      /* ---------------------------------------------------------------
         Shopify section editor events
      --------------------------------------------------------------- */
      onLoad: function () { this.open(); },
      onSelect: function () { this.open(); },
      onDeselect: function () { this.close(); }
    });

    return SidebarHub;
  })();

  function domReady(cb) {
    if (document.readyState !== 'loading') { cb(); }
    else { document.addEventListener('DOMContentLoaded', cb); }
  }

  domReady(function () {
    if (window.theme && window.theme.sections) {
      theme.sections.register('sidebar-hub', SidebarHub);
    } else {
      var hub = document.getElementById('SidebarHub');
      if (hub) { new SidebarHub(hub); }
    }
  });

})();
